import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { formatNumber } from '../lib/format';

interface KPICardProps {
  title: string;
  value: string | number;
  change: number;
  currency?: string;
  threshold?: {
    green: number;
    yellow: number;
  };
  className?: string;
  /** When provided, renders an "Ask DataPilot" affordance on the card. */
  onAskAssistant?: () => void;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  currency = '€',
  threshold,
  className = '',
  onAskAssistant,
}) => {
  const { t } = useTranslation();
  const isPositive = change >= 0;

  const getBackgroundColor = () => {
    if (!threshold) return 'bg-accent-50 dark:bg-accent-900/20';
    if (change >= threshold.green) return 'bg-green-50 dark:bg-green-900/20';
    if (change >= threshold.yellow) return 'bg-yellow-50 dark:bg-yellow-900/20';
    return 'bg-red-50 dark:bg-red-900/20';
  };

  const getTrendColor = () => {
    if (isPositive) return 'text-green-600 dark:text-green-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div
      className={`
        ${getBackgroundColor()}
        rounded-lg border border-slate-200 dark:border-slate-700
        p-6 shadow-sm transition-shadow hover:shadow-md
        ${className}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {title}
          </p>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
            {currency}{typeof value === 'number' ? formatNumber(value, 2) : value}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`
              flex items-center gap-1 rounded-full px-3 py-1
              ${isPositive ? 'bg-green-100 dark:bg-green-900/50' : 'bg-red-100 dark:bg-red-900/50'}
            `}
          >
            {isPositive ? (
              <TrendingUp className={`h-4 w-4 ${getTrendColor()}`} />
            ) : (
              <TrendingDown className={`h-4 w-4 ${getTrendColor()}`} />
            )}
            <span className={`text-sm font-medium ${getTrendColor()}`}>
              {Math.abs(change).toFixed(1)}%
            </span>
          </div>
          {onAskAssistant && (
            <button
              type="button"
              onClick={onAskAssistant}
              aria-label={t('datapilot.openChat', 'Ask DataPilot')}
              title={t('datapilot.openChat', 'Ask DataPilot')}
              className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-accent/10 hover:text-accent"
            >
              <Sparkles className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
